import React from 'react';
import toast from 'react-hot-toast';
import CorrectionModalBase from './CorrectionModalBase';
import api from '../services/api';

// Predefiniowane powody korekty faktury zakupowej
const CORRECTION_REASONS = [
  { value: '', label: '-- Wybierz powód korekty --' },
  { value: 'blad_ceny', label: 'Błąd w cenie' },
  { value: 'blad_ilosci', label: 'Błąd w ilości' },
  { value: 'zwrot_towaru', label: 'Zwrot towaru do dostawcy' },
  { value: 'reklamacja', label: 'Reklamacja' },
  { value: 'rabat_potransakcyjny', label: 'Rabat potransakcyjny' },
  { value: 'niezgodnosc_dostawy', label: 'Niezgodność z dostawą' },
  { value: 'uszkodzony_towar', label: 'Uszkodzony towar' },
  { value: 'pomylka_dostawcy', label: 'Pomyłka dostawcy' },
  { value: 'inne', label: 'Inny powód' }
];

// Formy płatności
const PAYMENT_METHODS = [
  { value: 'przelew', label: 'Przelew bankowy' },
  { value: 'gotowka', label: 'Gotówka' },
  { value: 'kompensata', label: 'Kompensata' },
  { value: 'karta', label: 'Karta płatnicza' },
  { value: 'potrącenie', label: 'Potrącenie z kolejnej faktury' },
  { value: 'inna', label: 'Inna' }
];

// --- Configuration callbacks ---

const invoiceInfoRows = (invoice) => [
  { label: 'Data faktury', value: invoice?.data_faktury || '-' },
  { label: 'Wartość faktury (brutto)', value: `${parseFloat(invoice?.suma_brutto || invoice?.wartosc_brutto || 0).toFixed(2)} zł` },
  { label: 'Dostawca', value: invoice?.dostawca_nazwa || invoice?.nazwa_dostawcy || '-' }
];

const itemSubtitle = (item) => (
  <>
    {item.kod_produktu && <span className="me-1">Kod: {item.kod_produktu}</span>}
    {item.ean && <span className="me-1">EAN: {item.ean}</span>}
    <span>VAT: {item.stawka_vat}%</span>
  </>
);

const calculateRowDifference = (item, sel) => {
  const stawkaVat = parseFloat(item.stawka_vat) || 23;
  const originalNetto = parseFloat(item.ilosc) * parseFloat(item.cena_netto);
  const newNetto = (sel.quantity || 0) * (sel.newPrice || 0);
  const roznicaNetto = newNetto - originalNetto;
  return roznicaNetto * (1 + stawkaVat / 100);
};

const calculateTotal = (items, selectedItems) => {
  let total = 0;
  items.forEach(item => {
    const sel = selectedItems[item.id];
    if (sel?.selected) {
      const stawkaVat = parseFloat(item.stawka_vat) || 23;
      const originalNetto = parseFloat(item.ilosc) * parseFloat(item.cena_netto);
      const newNetto = sel.quantity * sel.newPrice;
      const roznicaNetto = newNetto - originalNetto;
      total += roznicaNetto * (1 + stawkaVat / 100);
    }
  });
  return total;
};

const buildCorrectionItem = (item, sel, { iloscKorekty, originalQty, newQty, getFullReason }) => {
  const originalPrice = parseFloat(item.cena_netto);
  const stawkaVat = parseFloat(item.stawka_vat) || 23;

  return {
    pozycja_oryginalna_id: item.id,
    produkt_id: item.produkt_id,
    nazwa_produktu: item.nazwa_produktu,
    kod_produktu: item.kod_produktu,
    ean: item.ean,
    jednostka: item.jednostka || 'szt',
    ilosc: iloscKorekty,
    ilosc_oryginalna: originalQty,
    ilosc_po_korekcie: newQty,
    cena_netto: sel.newPrice,
    cena_oryginalna_netto: originalPrice,
    stawka_vat: stawkaVat,
    powod: sel.reason || getFullReason()
  };
};

const buildSubmitBody = ({ correctionItems, getFullReason, paymentMethod, extraState, authUser }) => ({
  pozycje: correctionItems,
  przyczyna_korekty: getFullReason(),
  numer_zewnetrzny: extraState.externalCorrectionNumber?.trim() || null,
  forma_platnosci: paymentMethod,
  termin_platnosci_dni: extraState.paymentDays,
  create_wz: true,
  created_by: authUser?.login || 'admin'
});

const onSuccessData = (apiData, { correctionItems, getFullReason, paymentMethod, extraState, invoice }) => {
  let successMessage = `Korekta ${apiData.numer_korekty} została utworzona pomyślnie!`;
  if (apiData.wz_document_number) {
    successMessage += ` Automatycznie utworzono dokument WZ: ${apiData.wz_document_number}`;
  }

  return {
    correctionData: {
      ...apiData,
      items: correctionItems,
      reason: getFullReason(),
      paymentMethod: paymentMethod,
      externalNumber: extraState.externalCorrectionNumber,
      invoice: invoice,
      wz_document_number: apiData.wz_document_number,
      wz_id: apiData.wz_id
    },
    successMessage
  };
};

// Print handler - PDF from backend
const handlePrint = ({ correctionData }) => {
  const openPdf = async () => {
    try {
      const response = await api.get(`/purchase-invoices/${correctionData.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Nie udalo sie wygenerowac PDF korekty.');
    }
  };
  openPdf();
};

// Payment section renderer (reason dropdown + external number field)
const renderPaymentSection = ({ reason, setReason, customReason, setCustomReason, correctionReasons, extraState, setExtraState }) => (
  <>
    <div className="col-md-6">
      <label className="form-label mb-1" style={{ fontSize: '10px' }}>
        Powód korekty: <span className="text-danger">*</span>
      </label>
      <select
        className="form-select form-select-sm"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={{ fontSize: '11px' }}
      >
        {correctionReasons.map(r => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      {reason === 'inne' && (
        <input
          type="text"
          className="form-control form-control-sm mt-1"
          placeholder="Opisz powód korekty..."
          value={customReason}
          onChange={(e) => setCustomReason(e.target.value)}
          style={{ fontSize: '11px' }}
        />
      )}
    </div>
    <div className="col-md-6">
      <label className="form-label mb-1" style={{ fontSize: '10px' }}>
        Numer zewnętrzny korekty (od dostawcy):
      </label>
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="np. KOR/2026/001 lub numer z dokumentu dostawcy"
        value={extraState.externalCorrectionNumber}
        onChange={(e) => setExtraState(prev => ({ ...prev, externalCorrectionNumber: e.target.value }))}
        style={{ fontSize: '11px' }}
      />
    </div>
  </>
);

// Extra fields renderer (payment method dropdown + payment days)
const renderExtraFields = ({ extraState, setExtraState, paymentMethod, setPaymentMethod, disabled }) => (
  <div className="row mb-2">
    <div className="col-md-6">
      <label className="form-label mb-1" style={{ fontSize: '10px' }}>
        Forma płatności/rozliczenia:
      </label>
      <select
        className="form-select form-select-sm"
        value={paymentMethod}
        onChange={(e) => setPaymentMethod(e.target.value)}
        style={{ fontSize: '11px' }}
        disabled={disabled}
      >
        {PAYMENT_METHODS.map(pm => (
          <option key={pm.value} value={pm.value}>{pm.label}</option>
        ))}
      </select>
    </div>
    <div className="col-md-6">
      <label className="form-label mb-1" style={{ fontSize: '10px' }}>
        Termin płatności (dni):
      </label>
      <input
        type="number"
        className="form-control form-control-sm"
        value={extraState.paymentDays}
        onChange={(e) => setExtraState(prev => ({ ...prev, paymentDays: parseInt(e.target.value) || 0 }))}
        min="0"
        max="365"
        style={{ fontSize: '11px' }}
        disabled={disabled}
      />
    </div>
  </div>
);

// --- Component ---

const PurchaseInvoiceCorrectionModal = (props) => (
  <CorrectionModalBase
    {...props}
    correctionReasons={CORRECTION_REASONS}
    title={(invoice) => `Korekta faktury zakupowej ${invoice?.numer_faktury || `#${invoice?.id}`}`}
    apiBase="purchase-invoices"
    itemsKey="items"
    priceField="cena_netto"
    priceColumnLabels={{ original: 'Cena netto oryg.', new: 'Nowa cena netto' }}
    invoiceInfoRows={invoiceInfoRows}
    itemSubtitle={itemSubtitle}
    calculateRowDifference={calculateRowDifference}
    calculateTotal={calculateTotal}
    differenceColorClasses={{ negative: 'text-success', positive: 'text-danger' }}
    summaryColors={{ refund: 'var(--success-light)', surcharge: 'var(--danger-light)' }}
    summaryLabels={{ refund: 'Kwota do odzyskania od dostawcy:', surcharge: 'Kwota do dopłaty dostawcy:' }}
    summaryTextClasses={{ refund: 'text-success', surcharge: 'text-danger' }}
    buildCorrectionItem={buildCorrectionItem}
    buildSubmitBody={buildSubmitBody}
    onSuccessData={onSuccessData}
    renderPaymentSection={renderPaymentSection}
    renderExtraFields={renderExtraFields}
    handlePrint={handlePrint}
    defaultPaymentMethod="przelew"
    extraStateDefaults={{ externalCorrectionNumber: '', paymentDays: 14 }}
  />
);

export default PurchaseInvoiceCorrectionModal;
